import { omit } from "lodash";
import { Dispatch, SetStateAction, useCallback } from "react";
import messages from "messages";
import { Form } from "react-bootstrap";
// components
import ActionsBar from "../../../../../actions-bar/ActionsBar";
import PlanFieldLabel from "../plan-field-label";
// styles
import styles from "./styles.module.scss";
// utils
import getClasses from "utils/getClasses";

type Props<T> = {
  className?: string;
  readOnly?: boolean;
  value: T;
  setValue?: Dispatch<SetStateAction<T>>;
};

const KEY = "behaviors";

const BehaviorFields = <T,>({
  className,
  readOnly,
  value,
  setValue,
}: Props<T>) => {
  const onDelete = useCallback(
    (i: number) =>
      setValue &&
      setValue((s) => {
        if (s[KEY]?.length <= 1) {
          return omit(s as any, KEY) as typeof s;
        }
        return {
          ...s,
          [KEY]: [
            ...(s[KEY] || []).slice(0, i),
            ...(s[KEY] || []).slice(i + 1),
          ],
        };
      }),
    [setValue],
  );

  const onCreate = useCallback(
    () =>
      setValue &&
      setValue((s) => ({
        ...s,
        [KEY]: !s[KEY] ? Array(2).fill("") : [...(s[KEY] || []), ""],
      })),
    [setValue],
  );

  return (
    <Form.Group
      className={getClasses(
        className,
        readOnly && styles["behavior-fields--readonly"],
      )}
      {...(readOnly && { as: "ul" })}
    >
      <PlanFieldLabel
        label={messages.behavioralDefinitions}
        readOnly={readOnly}
      />
      {readOnly
        ? (value[KEY] || []).map((el: string, index: number) => (
            <li key={index} className={styles["behavior-fields__li"]}>
              {el}
            </li>
          ))
        : (value[KEY] || [""]).map((el: string, index: number) => (
            <div
              key={index}
              className={styles["behavior-fields__input-container"]}
            >
              <Form.Control
                type={"text"}
                value={el}
                onChange={(e) =>
                  setValue &&
                  setValue((s) => ({
                    ...s,
                    [KEY]: [
                      ...(value[KEY] || []).slice(0, index),
                      e.target.value,
                      ...(value[KEY] || []).slice(index + 1),
                    ],
                  }))
                }
                placeholder={messages.enterYourDescription}
                readOnly={readOnly}
                plaintext={readOnly}
              />
              <ActionsBar
                onDelete={
                  !!el || value[KEY]?.length > 1 || index !== 0
                    ? () => onDelete(index)
                    : undefined
                }
                onCreate={onCreate}
              />
            </div>
          ))}
    </Form.Group>
  );
};

export default BehaviorFields;
